// src/App.js
import React, { useState, useEffect } from 'react';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Home from './pages/Home';
import MapPage from './pages/MapPage';
import AboutPage from './pages/AboutPage';
import { AuthProvider } from "./context/AuthContext";
import Register from './pages/Register';
import Login from './pages/Login';
import Logout from './pages/Logout';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/Profile';
import MainNavigation from './components/MainNavigation';
import SettingsDrawer from './components/SettingsDrawer';
import Sysop from "./pages/Sysop";
import ProtectedRoute from "./components/ProtectedRoute";
import AccountActionPage from './pages/AccountActionPage';
import ActivityTracker from './components/ActivityTracker';

// Your existing theme definitions …
const themes = {
  light: createTheme({ palette: { mode: 'light' } }),
  dark: createTheme({
    palette: {
      mode: 'dark',
      primary: { main: '#00C853' },
      secondary: { main: '#D500F9' },
      background: { default: '#0e1330', paper: '#1a2347' },
      text: { primary: '#ffffff', secondary: '#b0b6c5' }
    },
    typography: { fontFamily: 'Roboto, Helvetica, Arial, sans-serif' },
  }),
  // other themes...
};

function App() {
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [themeName, setThemeName] = useState('dark');
  const [userLat, setUserLat] = useState(null);
  const [userLon, setUserLon] = useState(null);
  const [selectedDrone, setSelectedDrone] = useState('phantom');
  const [refreshInterval, setRefreshInterval] = useState(0);
  const [address, setAddress] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [centerCoords, setCenterCoords] = useState(null);

  const handleFindMe = (optsOrCb, maybeCb) => {
    const opts = typeof optsOrCb === 'function' ? {} : (optsOrCb || {});
    const cb = typeof optsOrCb === 'function' ? optsOrCb : maybeCb;
    const fillOnly = !!opts.fillOnly;

    const apply = (lat, lon) => {
      if (!fillOnly) {
        setUserLat(lat);
        setUserLon(lon);
        localStorage.setItem('userLocation', JSON.stringify({ lat, lon }));
        setRefreshCounter((c) => c + 1);
      }
      if (cb) cb(lat, lon);
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => apply(pos.coords.latitude, pos.coords.longitude),
        () => alert('Unable to retrieve location.')
      );
    } else {
      alert('Geolocation not supported by this browser.');
    }
  };

  const handleHomeAddressSearch = (lat, lon) => {
    setUserLat(lat);
    setUserLon(lon);
    localStorage.setItem('userLocation', JSON.stringify({ lat, lon }));
    setAddress(`${lat.toFixed(5)}, ${lon.toFixed(5)}`);
    setRefreshCounter((c) => c + 1);
  };

  const handleMapAddressCenter = (latNum, lonNum) => {
    setCenterCoords({ lat: latNum, lon: lonNum });
  };

  const theme = themes[themeName] || themes.light;

  return (
  <AuthProvider>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <MainNavigation
          onSettingsClick={() => setDrawerOpen(true)}
          userLat={userLat}
          userLon={userLon}
          address={address}
          setAddress={setAddress}
          onFindMe={handleFindMe}
          onHomeAddressSearch={handleHomeAddressSearch}
          onMapAddressCenter={handleMapAddressCenter}
        />
        <SettingsDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          themeName={themeName}
          setThemeName={setThemeName}
          userLat={userLat}
          userLon={userLon}
          setUserLat={setUserLat}
          setUserLon={setUserLon}
          selectedDrone={selectedDrone}
          setSelectedDrone={setSelectedDrone}
          refreshInterval={refreshInterval}
          setRefreshInterval={setRefreshInterval}
          address={address}
          setAddress={setAddress}
        />
        <Routes>
          <Route
            path="/"
            element={
              <Home
                userLat={userLat}
                userLon={userLon}
                selectedDrone={selectedDrone}
                onLocationSelect={handleHomeAddressSearch}
              />
            }
          />
          <Route path="/map" element={<MapPage centerCoords={centerCoords} />} />
          <Route path="/about" element={<AboutPage />} />
		      <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/account-action" element={<AccountActionPage />} />
          
          {/* Protected Routes */}
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/sysop" element={<ProtectedRoute requiredRole="admin"><Sysop /></ProtectedRoute>} />
        </Routes>
      </Router>
    </ThemeProvider>
	</AuthProvider>
  );
}

export default App;
